import defaultRounds from './defaultRounds';
import { Round, RoundState, Team } from './pages/Game/Game';

export type LocalState = {
	currentRoundIndex: number;
	roundState: RoundState;
	scores: Record<Team, number>;
	selectedQuestionIndex: [number, number] | null;
	dailyDoubleWager: number | null;
	finalJeopardyWagers: [number, number] | null;
	rounds: Round[];
	zoom: number;
}

const getLocalStateItem = <K extends keyof LocalState>(
	key: K,
	defaultValue: LocalState[K],
): LocalState[K] => {
	const storedValue = localStorage.getItem(key);
	if (storedValue === null) {
		localStorage.setItem(key, JSON.stringify(defaultValue));
		return defaultValue;
	}
	try {
		const parsed = JSON.parse(storedValue);
		return parsed;
	} catch (e) {
		localStorage.setItem(key, JSON.stringify(defaultValue));
		return defaultValue;
	}
};

export const getLocalState = (): LocalState => {
	const rounds = getLocalStateItem('rounds', defaultRounds);
	const currentRoundIndex = getLocalStateItem('currentRoundIndex', 0);
	const currentRound = rounds[currentRoundIndex];
	const defaultRoundState: RoundState = 'finalJeopardy' in currentRound
		? [[false]]
		: currentRound.categories.map(
			category => category.questions.map(() => false)
		);

	let roundState = getLocalStateItem('roundState', defaultRoundState);
	const roundStateHasWrongDimensions =
		'finalJeopardy' in currentRound
		? (
			roundState.length !== 1
			|| roundState[0].length !== 1
		)
		: (
			roundState.length !== currentRound.categories.length
			|| roundState.some(categoryState => categoryState.length !== currentRound.categories[0].questions.length)
		);
	if (roundStateHasWrongDimensions) {
		roundState = defaultRoundState;
		setLocalState('roundState', roundState);
	}

	let selectedQuestionIndex = getLocalStateItem('selectedQuestionIndex', null);
	const selectedQuestionOutOfBounds =
		'finalJeopardy' in currentRound
			? (
				selectedQuestionIndex !== null
			)
			: (
				selectedQuestionIndex !== null
					&& (
						selectedQuestionIndex[0] >= currentRound.categories.length
						|| selectedQuestionIndex[1] >= currentRound.categories[selectedQuestionIndex[0]].questions.length
					)
			);
	if (selectedQuestionOutOfBounds) {
		selectedQuestionIndex = null;
		setLocalState('selectedQuestionIndex', selectedQuestionIndex);
	}

	return {
		rounds,
		currentRoundIndex,
		roundState,
		scores: getLocalStateItem('scores', {
			A: 0,
			B: 0,
		}),
		selectedQuestionIndex,
		dailyDoubleWager: getLocalStateItem('dailyDoubleWager', null),
		finalJeopardyWagers: getLocalStateItem('finalJeopardyWagers', null),
		zoom: getLocalStateItem('zoom', 1),
	};
};

export const setLocalState = <K extends keyof LocalState>(
	key: K,
	value: LocalState[K],
): void => {
	localStorage.setItem(key, JSON.stringify(value));
};

export const clearGameState = () => {
	const rounds = getLocalStateItem('rounds', defaultRounds);
	const zoom = getLocalStateItem('zoom', 1);
	localStorage.clear();
	setLocalState('rounds', rounds);
	setLocalState('zoom', zoom);
};
